import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useRealtimeConnection, useRealtimeStateStore } from "./useConnectionState";
import {
    RealtimeEvents,
    RealtimeHubMethods,
    WorkItemAddedNotificationSchema,
    WorkItemCompletedNotificationSchema,
    WorkItemSnoozedNotificationSchema,
    WorkItemLockedNotificationSchema,
    WorkItemUnlockedNotificationSchema,
    WorkItemEditedNotificationSchema,
    type WorkItemAddedNotification,
    type WorkItemCompletedNotification,
    type WorkItemSnoozedNotification,
    type WorkItemLockedNotification,
    type WorkItemUnlockedNotification,
    type WorkItemEditedNotification,
} from "./notifications";
import {
    patchWorkItemCompletedInWorkflowDetail,
    patchWorkItemSnoozedInWorkflowDetail,
    patchWorkItemLockedInWorkflowDetail,
    patchWorkItemUnlockedInWorkflowDetail,
    patchWorkItemEditedInWorkflowDetail,
} from "./cachePatches";
import { createInvalidationCoalescer } from "./invalidationCoalescer";
import { watchRealtimeLifecycle } from "./realtimeLifecycle";
import { workflowTimelineKeys } from "../workflows/timeline";

// ---------------------------------------------------------------------------
// Workflow-detail realtime subscription
//
// Two distinct responsibilities flow through one hook:
//
//   1. **Group membership.** The connection auto-joins the user's queue
//      groups on connect; workflow groups are client-driven. We invoke
//      `JoinWorkflow(workflowId)` on every transition into "connected"
//      and `LeaveWorkflow(workflowId)` on the matching cleanup so the
//      server-side group membership tracks the detail view's mounted
//      lifetime exactly. The re-join on reconnect is intentional: Azure
//      SignalR Service assigns a new connection id, so any prior group
//      attachment is lost and must be re-established. The join runs
//      inside `watchRealtimeLifecycle`, which also refetches the detail
//      and timeline after each gap (see "Reconnect semantics" on the hook).
//
//   2. **Cache reconciliation.** Workflow events are filtered to the
//      mounted `workflowId` — the same connection may receive events
//      for other workflows via overlapping queue group fan-out, but
//      this hook only touches the detail cache for the workflow whose
//      detail view is actually open.
//
//      Slice 6 split this into two paths (`docs/REALTIME.md`):
//        * `WorkItemAdded` → invalidate the detail key (the notification
//          lacks the full WorkItem fields needed to render a new row).
//        * `WorkItemCompleted` → patch the cached `Workflow.workItems`
//          row in place via `setQueriesData`. No refetch fires.
//
// Queue list caches are owned by `useQueueRealtime`. This hook only
// touches the workflow detail cache, so the two hooks' patches never
// overlap.
// ---------------------------------------------------------------------------

export interface UseWorkflowRealtimeOptions {
    readonly workflowId: string;
    readonly entityType: string;
    readonly entityId: string;
}

function safeParseAdded(payload: unknown): WorkItemAddedNotification | null {
    const parsed = WorkItemAddedNotificationSchema.safeParse(payload);
    return parsed.success ? parsed.data : null;
}

function safeParseCompleted(payload: unknown): WorkItemCompletedNotification | null {
    const parsed = WorkItemCompletedNotificationSchema.safeParse(payload);
    return parsed.success ? parsed.data : null;
}

function safeParseSnoozed(payload: unknown): WorkItemSnoozedNotification | null {
    const parsed = WorkItemSnoozedNotificationSchema.safeParse(payload);
    return parsed.success ? parsed.data : null;
}

function safeParseLocked(payload: unknown): WorkItemLockedNotification | null {
    const parsed = WorkItemLockedNotificationSchema.safeParse(payload);
    return parsed.success ? parsed.data : null;
}

function safeParseUnlocked(payload: unknown): WorkItemUnlockedNotification | null {
    const parsed = WorkItemUnlockedNotificationSchema.safeParse(payload);
    return parsed.success ? parsed.data : null;
}

function safeParseEdited(payload: unknown): WorkItemEditedNotification | null {
    const parsed = WorkItemEditedNotificationSchema.safeParse(payload);
    return parsed.success ? parsed.data : null;
}

/**
 * Subscribes the detail view to its workflow's realtime channel.
 *
 * Mounting contract:
 *   - Call inside the component that owns the workflow detail view,
 *     once per mounted workflow. The hook is a no-op when any of
 *     `workflowId`, `entityType`, or `entityId` are empty strings —
 *     a routing transition between detail views won't crash and the
 *     join only fires once the URL parameters have hydrated.
 *
 * Reconnect semantics:
 *   - Group membership is re-acquired automatically when the realtime
 *     connection state returns to `"connected"` after a reconnect (a new
 *     connection id has lost its groups). Once the re-join settles, the
 *     hook invalidates the workflow detail and timeline, because pushes
 *     sent during the gap are lost.
 *   - While the hub is not connected, the hook invalidates the detail and
 *     timeline every ~15 s on a visible tab (`watchRealtimeLifecycle`).
 *     While the hub is connected, no timer runs: the detail has no focus
 *     refetch, so a dropped push is picked up by a later add event, or on
 *     remount once the data is 30 s stale.
 *   - The hook watches the state store imperatively, so a state tick
 *     re-joins the group without re-rendering the caller and without
 *     detaching and re-attaching the event handlers.
 */
export function useWorkflowRealtime({
    workflowId,
    entityType,
    entityId,
}: UseWorkflowRealtimeOptions): void {
    const connection = useRealtimeConnection();
    const stateStore = useRealtimeStateStore();
    const queryClient = useQueryClient();

    useEffect(() => {
        if (connection === null) return;
        if (workflowId.length === 0) return;
        if (entityType.length === 0 || entityId.length === 0) return;

        const detailKey = ["workflows", "detail", workflowId, entityType, entityId] as const;
        const timelineKey = workflowTimelineKeys.detail(workflowId, entityType, entityId);

        // Timeline cache invalidation strategy. The audit feed is
        // append-only, so any mutation that emits a new ledger event
        // (add/complete/snooze) makes the cached timeline stale. The
        // realtime hub doesn't itself carry the activity record — it
        // only signals "something happened" — so the cheapest correct
        // path is to invalidate the timeline query: an active observer
        // refetches the new events from the BFF at once, an inactive one
        // when it next mounts. The timeline has no focus refetch.
        // Patching the cache in place would require the realtime layer to
        // synthesize an event shape, which is more coupling than the
        // refresh cadence justifies.
        //
        // Invalidations go through a coalescer: a completion cascade
        // delivers several events in one tick, and the detail and timeline
        // observers should refetch once for the burst, not once per event.
        const invalidation = createInvalidationCoalescer(queryClient);
        const invalidateTimeline = (): void => {
            invalidation.invalidate(timelineKey);
        };

        const handleAdded = (payload: unknown): void => {
            const notification = safeParseAdded(payload);
            if (notification === null) return;
            if (notification.workflowId !== workflowId) return;
            // Invalidate-and-refetch: the notification doesn't carry
            // the full WorkItem shape needed to setQueryData a new row
            // into the timeline. The follow-on refetch brings the
            // canonical item, including any server-side enrichment
            // (createdByDisplayName resolution, routingTags, …).
            invalidation.invalidate(detailKey);
            invalidateTimeline();
        };

        const handleCompleted = (payload: unknown): void => {
            const notification = safeParseCompleted(payload);
            if (notification === null) return;
            if (notification.workflowId !== workflowId) return;
            // Optimistic patch: the notification carries every field a
            // completion changes (`completedAt`, `completedBy`,
            // `completedByDisplayName`), so the detail is not refetched.
            // It refetches on a later add event, after a hub gap, or on
            // remount once stale. No timer refetches it while the hub is up.
            patchWorkItemCompletedInWorkflowDetail(queryClient, notification);
            invalidateTimeline();
        };

        const handleSnoozed = (payload: unknown): void => {
            const notification = safeParseSnoozed(payload);
            if (notification === null) return;
            if (notification.workflowId !== workflowId) return;
            // Optimistic patch: snooze only changes `dueDate`, and the
            // notification carries the post-state. Same shape as the
            // completion patch above.
            patchWorkItemSnoozedInWorkflowDetail(queryClient, notification);
            invalidateTimeline();
        };

        const handleLocked = (payload: unknown): void => {
            const notification = safeParseLocked(payload);
            if (notification === null) return;
            if (notification.workflowId !== workflowId) return;
            // Optimistic patch: the lock event carries the full post-state
            // (holder + expiry), so the manual-navigation banner renders
            // without a refetch. A lock emits no ledger event, so the
            // timeline is deliberately left alone.
            patchWorkItemLockedInWorkflowDetail(queryClient, notification);
        };

        const handleUnlocked = (payload: unknown): void => {
            const notification = safeParseUnlocked(payload);
            if (notification === null) return;
            if (notification.workflowId !== workflowId) return;
            patchWorkItemUnlockedInWorkflowDetail(queryClient, notification);
        };

        const handleEdited = (payload: unknown): void => {
            const notification = safeParseEdited(payload);
            if (notification === null) return;
            if (notification.workflowId !== workflowId) return;
            // Optimistic patch: a note edit only changes `body`, and the
            // notification carries the post-state. The edit emits a ledger
            // event (note.edited), so the timeline is invalidated too.
            patchWorkItemEditedInWorkflowDetail(queryClient, notification);
            invalidateTimeline();
        };

        connection.on(RealtimeEvents.workItemAdded, handleAdded);
        connection.on(RealtimeEvents.workItemCompleted, handleCompleted);
        connection.on(RealtimeEvents.workItemSnoozed, handleSnoozed);
        connection.on(RealtimeEvents.workItemLocked, handleLocked);
        connection.on(RealtimeEvents.workItemUnlocked, handleUnlocked);
        connection.on(RealtimeEvents.workItemEdited, handleEdited);

        // The join is only meaningful once the connection has reached the
        // Connected state. The lifecycle watcher joins when the hub is live:
        // now if it already is, otherwise on the transition into
        // "connected", and again after every reconnect. After a gap it then
        // calls `onResync` once, because pushes sent during the gap are
        // lost. While the hub is down it calls `onResync` every ~15 s on a
        // visible tab. Handlers stay bound throughout.
        let joined = false;
        const join = async (): Promise<void> => {
            joined = true;
            try {
                await connection.invoke(RealtimeHubMethods.joinWorkflow, workflowId);
            } catch {
                // Best-effort: a failed join means we miss this workflow's
                // group pushes until the next reconnect joins again. While
                // the hub is up, no timer refetches the detail. No
                // user-visible error is appropriate.
                joined = false;
            }
        };
        const stopLifecycle = watchRealtimeLifecycle(stateStore, {
            onLive: join,
            onResync: () => {
                invalidation.invalidate(detailKey);
                invalidateTimeline();
            },
        });

        return () => {
            stopLifecycle();
            invalidation.dispose();
            connection.off(RealtimeEvents.workItemAdded, handleAdded);
            connection.off(RealtimeEvents.workItemCompleted, handleCompleted);
            connection.off(RealtimeEvents.workItemSnoozed, handleSnoozed);
            connection.off(RealtimeEvents.workItemLocked, handleLocked);
            connection.off(RealtimeEvents.workItemUnlocked, handleUnlocked);
            connection.off(RealtimeEvents.workItemEdited, handleEdited);
            if (joined) {
                connection
                    .invoke(RealtimeHubMethods.leaveWorkflow, workflowId)
                    .catch(() => {
                        // Leave is best-effort: if the connection has
                        // already dropped, the server-side membership
                        // disappeared with it; otherwise transient
                        // failures resolve themselves when the
                        // connection eventually closes.
                    });
            }
        };
    }, [connection, stateStore, workflowId, entityType, entityId, queryClient]);
}
